import { Injectable, WritableSignal, signal } from '@angular/core';
import { OrganisationFilterService } from 'app/layout/common/organisation-filter/organisation-filter.service';
import { Position, PositionControllerService } from 'app/position-api';
import { OrgNavigationService } from 'app/services/org-nav.service';
import { Observable, map, of, switchMap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PositionsService {
    private _positions: WritableSignal<Position[]> = signal([]);

    public get positions() {
        return this._positions.asReadonly();
    }

    private _position: WritableSignal<Position> = signal(null);

    public get position() {
        return this._position.asReadonly();
    }

    constructor(
        private api: PositionControllerService,
        private orgfilterService: OrganisationFilterService,
        private orgNav: OrgNavigationService
    ) {}

    getPositionById(id: string): Observable<Position> {
        return of(this._positions().find(position => position.id === id)).pipe(
            map((response: Position) => {
                this._position.set(response);

                return response;
            }),
            switchMap(response => {
                if (!response) {
                    return throwError('Could not found position with id of ' + id + '!');
                }

                return of(response);
            })
        );
    }

    createNewEmptyPosition(): Observable<Position> {
        const position: Position = {
            id: 'new',
        };
        this._position.set(position);

        return of(this.position());
    }

    getPositions(filter?: string): Observable<Position[]> {
        return this.api.getAllPositions().pipe(
            switchMap((positions: Position[]) => {
                // Filter positions if a filter is provided
                if (filter) {
                    positions = positions.filter(position =>
                        position.name.toLowerCase().includes(filter.toLowerCase())
                    );
                }

                // Set filtered positions into the BehaviorSubject (assuming you need this)
                this._positions.set(positions);

                // Return the filtered positions
                return of(positions);
            })
        );
    }

    addPositions(position: Position): Observable<any> {
        return this.api.createNewPosition(position).pipe(
            switchMap(result => {
                return this.getPositions().pipe(
                    map(positions => {
                        this.navigateToPosition(result.id);
                    })
                );
            })
        );
    }

    updatePosition(position: Position): Observable<any> {
        return this.api.updatePosition(position.id, position).pipe(
            switchMap(() => {
                return this.getPositions();
            })
        );
    }

    removePosition(position: Position): Observable<any> {
        return this.api._delete(position.id).pipe(
            switchMap(() => {
                return this.getPositions();
            })
        );
    }

    navigateToPosition(id: string): void {
        const baseUrl = this.orgNav.url.split(/[?#;]/)[0];
        const urls = baseUrl.split('/');
        if (urls.length >= 6) {
            urls[6] = id;
        } else {
            urls.push(id);
        }

        this.orgNav.navigate(urls);
    }
}
